import {groupBy} from '@benchling/js-utils';
import _ from 'underscore';

import fetchAllPaginated from 'lib/fetchAllPaginated';
import {getMaxRowsConfig} from 'notebook/structured-tables/util/checkTableCapacity';
import {Thunk} from 'redux-stores/Store';

import {AssayResult, isOversized} from './AssayRecordConstants';
import {
  countResultsBySchema,
  CountResultsBySchemaResponse,
  listAssayResults,
  ListAssayResultsResponse,
} from './AssayRecordDAO';
import {ServerResultsTableSpec} from './ResultsTableSpecConstants';

export enum FilteredAssayResultsActionType {
  FILTERED_ASSAY_RESULTS_FETCH_COMPLETED = 'FILTERED_ASSAY_RESULTS_FETCH_COMPLETED',
}

const MAX_FETCHED_RESULTS_PER_SCHEMA_SAFETY_LIMIT = 10_000;

export type Filters = Partial<
  Record<
    'bioentityId' | 'requestId' | 'storableId' | 'assayRunId' | 'automationOutputFileId' | 'studyId',
    string | undefined
  >
>;

export interface FilteredAssayResultsFetchCompletedAction {
  type: FilteredAssayResultsActionType.FILTERED_ASSAY_RESULTS_FETCH_COMPLETED;
  assayResults: Array<AssayResult>;
  filters: Filters;
  serverResultsTableSpecs: Array<ServerResultsTableSpec>;
}

function filteredAssayResultsFetchCompleted(
  assayResults: Array<AssayResult>,
  filters: Filters,
  serverResultsTableSpecs: Array<ServerResultsTableSpec>
): FilteredAssayResultsFetchCompletedAction {
  return {
    type: FilteredAssayResultsActionType.FILTERED_ASSAY_RESULTS_FETCH_COMPLETED,
    assayResults,
    filters,
    serverResultsTableSpecs,
  };
}

function getOversizedSchemaIds(resultCountBySchemaId: {[schemaId: string]: number}): Array<string> {
  return Object.keys(resultCountBySchemaId).filter((key) => isOversized(resultCountBySchemaId[key]));
}

async function listNonOversizedResults(
  filters: Filters,
  oversizedSchemaIds: Array<string>
): Promise<{assayResults: Array<AssayResult>}> {
  let assayResults: Array<AssayResult> = [];
  await fetchAllPaginated<ListAssayResultsResponse>(
    (nextToken) =>
      listAssayResults({
        nextToken,
        excludedSchemaIds: oversizedSchemaIds,
        ...filters,
        sort: 'createdAt:desc',
      }),
    (response) => {
      assayResults = assayResults.concat(response.assayResults);
      return response.assayResults;
    }
  );
  return {assayResults};
}

function getServerResultsTableSpecs(
  assayResults: Array<AssayResult>,
  resultCountBySchemaId: {[schemaId: string]: number}
): Array<ServerResultsTableSpec> {
  const assayResultsBySchemaId = _.groupBy(assayResults, 'schemaId');
  return Object.entries(resultCountBySchemaId).map(([schemaId, total]) => ({
    schemaId,
    total,
    assayResultIds: (assayResultsBySchemaId[schemaId] || []).map((result) => result.id),
    isOversized: isOversized(total),
  }));
}

/**
 * Fetch AssayResults with filters (ex: linked to entity, or request, run)
 *
 * If a table is oversized (i.e. >500 results), no results for that schema are loaded.
 *
 * To handle oversized results, return grouped by schema as Array<ServerResultsTableSpec>. This object has
 * information about the schema, is oversized, total # results, and results if not oversized.
 */
export function fetchFilteredAssayResults(filters: Filters): Thunk<Promise<void>> {
  return async (dispatch) => {
    const {resultCountBySchemaId}: CountResultsBySchemaResponse = await countResultsBySchema(filters);
    let assayResults: Array<AssayResult> = [];
    const hasResults = Object.values(resultCountBySchemaId).some((count) => count > 0);
    if (hasResults) {
      const oversizedSchemaIds = getOversizedSchemaIds(resultCountBySchemaId);
      const listResponse = await listNonOversizedResults(filters, oversizedSchemaIds);
      assayResults = listResponse.assayResults;
    }
    const serverResultsTableSpecs = getServerResultsTableSpecs(assayResults, resultCountBySchemaId);
    dispatch(filteredAssayResultsFetchCompleted(assayResults, filters, serverResultsTableSpecs));
  };
}

export interface ResultsBySchemaId {
  [schemaId: string]: Array<AssayResult>;
}

/**
 * Optional hook to transform the fetched results (grouped by schema) before they are truncated to the
 * per-schema row limit. Results makes no assumption about what the transform does — e.g. a caller may
 * reorder rows. Because a transform may not respect the per-schema fetch limit, providing one forces a
 * larger fetch up to a safety cap per schema (see `listResultsWithPerSchemaLimit`).
 */
export type PostProcessResultsBySchema = (resultsBySchema: ResultsBySchemaId) => ResultsBySchemaId;

/**
 * Query to list results, limiting the number of results loaded per schema.
 *
 * Results are loaded with pagination, 100 at a time. After the maximum number of results for a given schema
 * are loaded, further paginated calls will exclude the schema.
 *
 * Results are sorted by `created_at`; the loaded results will be the most recently created results.
 *
 * The default maximum number of rows is 500. See `getMaxRowsConfig`.
 *
 * This function may return more than the maximum number of results.
 *
 * When `fetchAll` is true, this query still applies a safety cap of
 * `MAX_FETCHED_RESULTS_PER_SCHEMA_SAFETY_LIMIT` per schema to avoid unbounded browser memory usage.
 * This mode is used when a caller post-processes results before truncation (e.g. reorders them), since the
 * final truncated set otherwise depends on the fetch order rather than the post-processed order.
 */
async function listResultsWithPerSchemaLimit(
  filters: Filters,
  fetchAll = false,
  maxFetchedResultsPerSchema = MAX_FETCHED_RESULTS_PER_SCHEMA_SAFETY_LIMIT
): Promise<ResultsBySchemaId> {
  const resultsBySchemaId: ResultsBySchemaId = {};

  const fetchMore = (
    resultsBySchemaId: {[schemaId: string]: Array<AssayResult>},
    nextToken: string
  ): Promise<ListAssayResultsResponse> => {
    const oversizedSchemaIds = fetchAll
      ? Object.keys(resultsBySchemaId).filter(
          (key) => resultsBySchemaId[key].length >= maxFetchedResultsPerSchema
        )
      : Object.keys(resultsBySchemaId).filter((key) => isOversized(resultsBySchemaId[key].length));
    return listAssayResults({
      nextToken,
      excludedSchemaIds: oversizedSchemaIds,
      ...filters,
      sort: 'createdAt:desc',
    });
  };

  let nextToken = '';
  let hasMore = true;

  while (hasMore) {
    const response = await fetchMore(resultsBySchemaId, nextToken);

    const newResultsBySchemaId = groupBy(response.assayResults, ({schemaId}) => schemaId);
    Object.entries(newResultsBySchemaId).forEach(([schemaId, newResults]) => {
      const existingResults = resultsBySchemaId[schemaId] ?? [];
      if (fetchAll) {
        const availableCapacity = maxFetchedResultsPerSchema - existingResults.length;
        if (availableCapacity <= 0) {
          return;
        }
        resultsBySchemaId[schemaId] = [...existingResults, ...newResults.slice(0, availableCapacity)];
        return;
      }
      resultsBySchemaId[schemaId] = [...existingResults, ...newResults];
    });

    nextToken = response.nextToken;
    hasMore = nextToken?.length > 0;
  }

  return resultsBySchemaId;
}

/**
 * @package
 * Helper split out from `fetchFilteredAssayResultsWithPerSchemaLimit` so the response and logic can be tested
 * seperately from redux.
 */
export async function getFilteredAssayResultsFetchCompletedAction(
  filters: Filters,
  postProcessResultsBySchema?: PostProcessResultsBySchema,
  maxFetchedResultsPerSchema = MAX_FETCHED_RESULTS_PER_SCHEMA_SAFETY_LIMIT
): Promise<FilteredAssayResultsFetchCompletedAction> {
  const maxResults = getMaxRowsConfig(false);

  // A post-process hook may reorder results, so the truncated set must be chosen from the
  // post-processed order rather than the fetch order — which requires fetching beyond the per-schema
  // display limit (up to a hard safety cap).
  const fetchedResultsBySchemaId = await listResultsWithPerSchemaLimit(
    filters,
    /* fetchAll */ postProcessResultsBySchema != null,
    maxFetchedResultsPerSchema
  );
  const resultsBySchemaId = postProcessResultsBySchema
    ? postProcessResultsBySchema(fetchedResultsBySchemaId)
    : fetchedResultsBySchemaId;

  const allResults = Object.values(resultsBySchemaId).flatMap((results) => results.slice(0, maxResults));

  const serverResultsTableSpecs = Object.entries(resultsBySchemaId).map(([schemaId, results]) => ({
    schemaId,
    total: Math.min(results.length, maxResults),
    assayResultIds: results.slice(0, maxResults).map((result) => result.id),
    isOversized: false,
    isTruncated: results.length >= maxResults,
  }));

  return filteredAssayResultsFetchCompleted(allResults, filters, serverResultsTableSpecs);
}

/**
 * Fetch AssayResults with filters (ex: linked to entity, or request, run)
 *
 * If a table is oversized (i.e. >500 results), only the 500 most recently created results of that schema are
 * returned.
 *
 * `postProcessResultsBySchema` optionally transforms the fetched results (grouped by schema) before
 * truncation. Providing it forces a larger fetch up to a per-schema safety cap (see
 * `getFilteredAssayResultsFetchCompletedAction`).
 */
export function fetchFilteredAssayResultsWithPerSchemaLimit(
  filters: Filters,
  postProcessResultsBySchema?: PostProcessResultsBySchema
): Thunk<Promise<void>> {
  return async (dispatch) => {
    dispatch(await getFilteredAssayResultsFetchCompletedAction(filters, postProcessResultsBySchema));
  };
}

export type FilteredAssayResultsAction = FilteredAssayResultsFetchCompletedAction;
